import type { AttendanceDayKind, AttendanceDayStatus } from "./attendance.ts";
import { client, handleRequest, handleRequestBody } from "./client.ts";
import { filenameFromDisposition } from "./rdos.ts";

export type TimesheetExportFormat = "pdf" | "csv";

export interface TimesheetWorksite {
	id: string;
	name: string;
	timezone: string;
}

export interface TimesheetSettings {
	weekday_pct: number;
	saturday_pct: number;
	sunday_pct: number;
	holiday_pct: number;
}

export interface TimesheetHoliday {
	date: string;
	name: string;
}

export interface TimesheetWorker {
	id: string;
	name: string;
	job_title: string;
	linked: boolean;
}

export interface TimesheetBucket {
	minutes: number;
	pct: number;
	equivalent_minutes: number;
}

export interface TimesheetOvertime {
	weekday: TimesheetBucket;
	saturday: TimesheetBucket;
	sunday: TimesheetBucket;
	holiday: TimesheetBucket;
}

export interface TimesheetDay {
	date: string;
	kind: AttendanceDayKind;
	status: AttendanceDayStatus;
	worked_minutes: number;
	overtime_minutes: number;
	overtime_pct: number;
	overridden: boolean;
}

export interface TimesheetRow {
	worker: TimesheetWorker;
	days_present: number;
	days_absent: number;
	days_medical_leave: number;
	days_day_off: number;
	days_pending: number;
	worked_minutes: number;
	normal_minutes: number;
	missing_minutes: number;
	overtime: TimesheetOvertime;
	overtime_minutes: number;
	overtime_equivalent_minutes: number;
	total_equivalent_minutes: number;
	days: TimesheetDay[];
}

export interface TimesheetTotals {
	worked_minutes: number;
	overtime_minutes: number;
	overtime_equivalent_minutes: number;
}

export interface TimesheetResponse {
	month: string;
	worksite: TimesheetWorksite;
	settings: TimesheetSettings;
	holidays: TimesheetHoliday[];
	rows: TimesheetRow[];
	totals: TimesheetTotals;
}

export interface TimesheetFile {
	blob: Blob;
	filename: string;
}

function timesheetPath(worksiteId: string): string {
	return `/worksites/${worksiteId}/timesheet`;
}

export function getTimesheet(
	worksiteId: string,
	month: string,
): Promise<TimesheetResponse> {
	return handleRequestBody(() =>
		client.get<TimesheetResponse>(timesheetPath(worksiteId), {
			params: { month },
		}),
	);
}

export async function downloadTimesheet(
	worksiteId: string,
	month: string,
	format: TimesheetExportFormat,
	fallbackFilename: string,
): Promise<TimesheetFile> {
	const response = await handleRequest(() =>
		client.get<Blob>(timesheetPath(worksiteId), {
			params: { month, format },
			responseType: "blob",
		}),
	);
	return {
		blob: response.data,
		filename: filenameFromDisposition(
			response.headers["content-disposition"],
			fallbackFilename,
		),
	};
}
